import styles from './create-new-registration-dialog.module.scss';
import { Col, Row } from 'reactstrap';
import { ChangeEvent, useContext, useState } from 'react';
import { makeCourseRequest, useCourseGetRequestHook } from '../../../requestUtils';
import { useParams } from 'react-router-dom';
import { CheckboxLabel, Dialog, DialogActions, DialogContent, DialogTitle, GrayOutlinedButton, LoadingButton, MultiLineTextField, SnackBarContext, TextField } from '@frontend-workspace/react-shared-lib';
import { ICourse } from '../../../_models/course';
import { useTranslation } from 'react-i18next';

/* eslint-disable-next-line */
export interface CreateNewRegistrationDialogProps {
  onClose: () => void,
  open: boolean,
  refreshData: () => void
}

export function CreateNewRegistrationDialog(props: CreateNewRegistrationDialogProps) {
  const { courseId } = useParams();
  const { data: course }: { data: ICourse } = useCourseGetRequestHook(`course/get/${courseId}`, undefined);

  const { t } = useTranslation();
  const { setSnackbarMessage } = useContext(SnackBarContext);
  const [loading, setLoading] = useState<boolean>(false);

  const [sendEmail, setSendEmail] = useState<boolean>(false);
  const [email, setEmail] = useState<string>("");
  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [mobile, setMobile] = useState<string>("");
  const [company, setCompany] = useState<string>("");
  const [address, setAddress] = useState<string>("");
  const [postCode, setPostCode] = useState<string>("");
  const [city, setCity] = useState<string>("");
  const [invoiceContactPerson, setInvoiceContactPerson] = useState<string>("");
  const [invoiceEmail, setInvoiceEmail] = useState<string>("");
  const [orderReference, setOrderReference] = useState<string>("");
  const [employeeNumber, setEmployeeNumber] = useState<string>("");
  const [comments, setComments] = useState<string>("");

  const handleSendEmailChange = (event: ChangeEvent<Element>, checked: boolean) => setSendEmail(checked);
  const handleEmailChange = (event: ChangeEvent<HTMLInputElement>) => setEmail(event.target.value);
  const handleFirstNameChange = (event: ChangeEvent<HTMLInputElement>) => setFirstName(event.target.value);
  const handleLastNameChange = (event: ChangeEvent<HTMLInputElement>) => setLastName(event.target.value);
  const handleMobileChange = (event: ChangeEvent<HTMLInputElement>) => setMobile(event.target.value);
  const handleCompanyChange = (event: ChangeEvent<HTMLInputElement>) => setCompany(event.target.value);
  const handleAddressChange = (event: ChangeEvent<HTMLInputElement>) => setAddress(event.target.value);
  const handlePostCodeChange = (event: ChangeEvent<HTMLInputElement>) => setPostCode(event.target.value);
  const handleCityChange = (event: ChangeEvent<HTMLInputElement>) => setCity(event.target.value);
  const handleInvoiceContactPersonChange = (event: ChangeEvent<HTMLInputElement>) => setInvoiceContactPerson(event.target.value);
  const handleInvoiceEmailChange = (event: ChangeEvent<HTMLInputElement>) => setInvoiceEmail(event.target.value);
  const handleOrderReferenceChange = (event: ChangeEvent<HTMLInputElement>) => setOrderReference(event.target.value);
  const handleEmployeeNumberChange = (event: ChangeEvent<HTMLInputElement>) => setEmployeeNumber(event.target.value);
  const handleCommentsChange = (event: ChangeEvent<HTMLInputElement>) => setComments(event.target.value);

  const missingFields = (): boolean => {
    const requiredFieldsFilled =
      email &&
      firstName &&
      lastName &&
      mobile &&
      company &&
      address &&
      postCode &&
      city
    return requiredFieldsFilled ? false : true;
  };

  const handleAddParticipant = () => {
    setLoading(true);
    const body = {
      person: {
        firstName: firstName,
        lastName: lastName,
        email: email,
        mobile: mobile,
        companyName: company
      },
      course: course,
      comments: comments,
      invoiceInfo: {
        contactPersonName: invoiceContactPerson,
        invoiceEmail: invoiceEmail,
        employeeNumber: employeeNumber,
        orderReference: orderReference
      },
      company: {
        companyName: company,
        address: address,
        postCode: postCode,
        city: city,
      },
      sendEmail: sendEmail
    }
    makeCourseRequest(`course/register`, 'POST', body).then(result => {
      setSnackbarMessage({ content: t('createNewRegistration.addedCourseParticipantToList'), type: "info", });
      setLoading(false);
      props.refreshData();
      props.onClose();
    }).catch(() => setSnackbarMessage({ content: t('createNewRegistration.couldNotAddCourseParticipantToList'), type: "error" }));
  };

  return (
    <Dialog
      open={props.open}
      maxWidth="lg"
      fullWidth>
      <DialogTitle className='mb-4'>
        {t('createNewRegistration.newCourseParticipant')}
      </DialogTitle>
      <DialogContent>
        <div className='d-flex mb-3'>
          <CheckboxLabel
            label={t('createNewRegistration.sendEmailWhenRegistering')}
            checked={sendEmail}
            onChange={handleSendEmailChange}
          />
        </div>
        <Row>
          <Col xl={6}>
            <div className='mb-3'>
              <TextField
                label={<span>{t('standard.email')} &#42;</span>}
                name='email'
                type='email'
                fullWidth
                required
                sx={{ minWidth: 300 }}
                value={email || ''}
                onChange={handleEmailChange}
              />
            </div>
            <div className='mb-3'>
              <TextField
                label={<span>{t('standard.firstName')} &#42;</span>}
                name='firstName'
                fullWidth
                required
                sx={{ minWidth: 300 }}
                value={firstName || ''}
                onChange={handleFirstNameChange}
              />
            </div>
            <div className='mb-3'>
              <TextField
                label={<span>{t('standard.lastName')} &#42;</span>}
                name='lastName'
                fullWidth
                required
                sx={{ minWidth: 300 }}
                value={lastName || ''}
                onChange={handleLastNameChange}
              />
            </div>
            <Row>
              <Col xl={5}>
                <div className='mb-3'>
                  <TextField
                    label={<span>{t('standard.mobileNo')} &#42;</span>}
                    name='mobile'
                    type='tel'
                    fullWidth
                    required
                    value={mobile || ''}
                    onChange={handleMobileChange}
                  />
                </div>
              </Col>
            </Row>
            <div className='mb-3'>
              <TextField
                label={<span>{t('standard.company')} &#42;</span>}
                name='company'
                fullWidth
                required
                sx={{ minWidth: 300 }}
                value={company || ''}
                onChange={handleCompanyChange}
              />
            </div>
            <Row>
              <Col xl={12}>
                <div className='mb-3'>
                  <TextField
                    label={<span>{t('standard.address')} &#42;</span>}
                    name='address'
                    fullWidth
                    required
                    sx={{ minWidth: 300 }}
                    value={address || ''}
                    onChange={handleAddressChange}
                  />
                </div>
                <Row>
                  <Col xl={4}>
                    <div className='mb-3'>
                      <TextField
                        label={<span>{t('standard.postCode')} &#42;</span>}
                        name='postCode'
                        fullWidth
                        required
                        value={postCode || ''}
                        onChange={handlePostCodeChange}
                      />
                    </div>
                  </Col>
                  <Col xl={8}>
                    <div className='mb-3'>
                      <TextField
                        label={<span>{t('standard.place')} &#42;</span>}
                        name='city'
                        fullWidth
                        required
                        value={city || ''}
                        onChange={handleCityChange}
                      />
                    </div>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Col>
          <Col xl={6}>
            <div className='mb-3'>
              <TextField
                label={t('standard.contactPersonForInvoice')}
                name='invoiceContactPerson'
                fullWidth
                sx={{ minWidth: 300 }}
                value={invoiceContactPerson || ''}
                onChange={handleInvoiceContactPersonChange}
              />
            </div>
            <div className='mb-3'>
              <TextField
                label={t('standard.invoiceEmail')}
                name='invoiceEmail'
                fullWidth
                sx={{ minWidth: 300 }}
                value={invoiceEmail || ''}
                onChange={handleInvoiceEmailChange}
              />
            </div>
            <div className='mb-3'>
              <TextField
                label={t('standard.orderReference')}
                name='orderReference'
                fullWidth
                sx={{ minWidth: 300 }}
                value={orderReference || ''}
                onChange={handleOrderReferenceChange}
              />
            </div>
            <Row>
              <Col xl={5}>
                <div className='mb-3'>
                  <TextField
                    label={t('standard.employeeNumber')}
                    name='employeeNumber'
                    fullWidth
                    value={employeeNumber || ''}
                    onChange={handleEmployeeNumberChange}
                  />
                </div>
              </Col>
            </Row>
            {/* Todo:: Geir ønsker org.nr */}
            <div className='mb-3'>
              <TextField
                label={t('standard.organisationNumber')}
                name='Org.no.'
                fullWidth
                sx={{ minWidth: 300 }}
                disabled
              // value={}
              // onChange={}
              />
            </div>
          </Col>
        </Row>
        <Row>
          <Col>
            <div className='mb-3'>
              <MultiLineTextField
                label={t('standard.otherComments')}
                name='comments'
                fullWidth
                minRows={1}
                value={comments || ''}
                onChange={handleCommentsChange}
              />
            </div>
          </Col>
        </Row>
      </DialogContent>
      <DialogActions>
        <LoadingButton
          variant="contained"
          size='large'
          color='secondary'
          onClick={handleAddParticipant}
          disabled={loading || missingFields()}
          loading={loading}
          className='minButtonWidth'>
          {loading ? '' : t('createNewRegistration.createNewCourseParticipant')}
        </LoadingButton>
        <GrayOutlinedButton
          variant="outlined"
          size='large'
          onClick={props.onClose}
          className='minButtonWidth'>
          {t('standard.cancel')}
        </GrayOutlinedButton>
      </DialogActions>
    </Dialog>
  );
}

export default CreateNewRegistrationDialog;
